/**
 * WorkOrderCreatePage — Full-featured work order creation form
 *
 * Reached via /work-orders/new. Supports pre-selecting location/asset
 * via query params (?locationId=X&assetId=Y).
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	Button,
	Input,
	Select,
	DatePicker,
	InputNumber,
	message,
	Card,
	Divider,
	Steps,
} from "antd";
import {
	ArrowLeftOutlined,
	EnvironmentOutlined,
	FileTextOutlined,
	UserOutlined,
	CheckCircleOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import {
	createWorkOrder,
	listVendors,
	getAssetsByLocation,
	type WorkOrderType,
	type WorkOrderPriority,
	type Vendor,
} from "@/services/work-order-service";
import {
	listMyVendors,
	type OrgVendorWithDetails,
} from "@/services/facility-link-service";
import { getOrganizationLocations } from "@/services/location-service";
import { getOrganizationUsers } from "@/services/user-service";
import "./WorkOrderCreatePage.scss";

const { TextArea } = Input;

const WORK_TYPES: { value: WorkOrderType; label: string; description: string }[] = [
	{ value: "EMERGENCY", label: "Emergency", description: "Immediate response required — safety or critical system failure" },
	{ value: "CORRECTIVE", label: "Corrective", description: "Fix a known issue or broken equipment" },
	{ value: "PREVENTIVE", label: "Preventive", description: "Scheduled maintenance to prevent future issues" },
	{ value: "INSPECTION", label: "Inspection", description: "Routine inspection or compliance check" },
];

const PRIORITIES: { value: WorkOrderPriority; label: string; color: string; sla: string }[] = [
	{ value: 1, label: "P1 — Emergency", color: "#ff4d4f", sla: "Response within 1 hour" },
	{ value: 2, label: "P2 — Urgent", color: "#fa8c16", sla: "Response within 4 hours" },
	{ value: 3, label: "P3 — High", color: "#faad14", sla: "Response within 24 hours" },
	{ value: 4, label: "P4 — Medium", color: "#1890ff", sla: "Response within 3 days" },
	{ value: 5, label: "P5 — Low", color: "#8c8c8c", sla: "Response within 7 days" },
];

interface LocationOption {
	id: number;
	label: string;
	address: string;
}

interface AssetOption {
	id: number;
	label: string;
}

interface UserOption {
	id: number;
	label: string;
	email: string;
}

type AssigneeType = "none" | "user" | "vendor" | "fl_vendor";

const WorkOrderCreatePage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	// Step tracking
	const [currentStep, setCurrentStep] = useState(0);

	// Location & Asset
	const [locations, setLocations] = useState<LocationOption[]>([]);
	const [assets, setAssets] = useState<AssetOption[]>([]);
	const [locationId, setLocationId] = useState<number | null>(null);
	const [assetId, setAssetId] = useState<number | null>(null);
	const [loadingLocations, setLoadingLocations] = useState(true);
	const [loadingAssets, setLoadingAssets] = useState(false);

	// Work details
	const [title, setTitle] = useState("");
	const [workType, setWorkType] = useState<WorkOrderType>("CORRECTIVE");
	const [priority, setPriority] = useState<WorkOrderPriority>(3);
	const [description, setDescription] = useState("");

	// Assignment
	const [assigneeType, setAssigneeType] = useState<AssigneeType>("none");
	const [users, setUsers] = useState<UserOption[]>([]);
	const [vendors, setVendors] = useState<Vendor[]>([]);
	const [flVendors, setFlVendors] = useState<OrgVendorWithDetails[]>([]);
	const [assignedToUserId, setAssignedToUserId] = useState<number | null>(null);
	const [assignedToVendorId, setAssignedToVendorId] = useState<number | null>(null);
	const [assignedToFlOrgVendorId, setAssignedToFlOrgVendorId] = useState<number | null>(null);
	const [loadingUsers, setLoadingUsers] = useState(false);

	// Estimates
	const [dueDate, setDueDate] = useState<any>(null);
	const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
	const [estimatedLaborHours, setEstimatedLaborHours] = useState<number | null>(null);

	// Submission
	const [submitting, setSubmitting] = useState(false);

	// Load locations on mount
	useEffect(() => {
		(async () => {
			try {
				const data = await getOrganizationLocations();
				const mapped: LocationOption[] = (data || []).map((loc: any) => ({
					id: loc.id,
					label: loc.name || loc.address || `Location #${loc.id}`,
					address: loc.address
						? `${loc.address}${loc.city ? `, ${loc.city}` : ""}${loc.state ? `, ${loc.state}` : ""}`
						: loc.address1
							? `${loc.address1}${loc.city ? `, ${loc.city}` : ""}${loc.state ? `, ${loc.state}` : ""}`
							: "",
				}));
				setLocations(mapped);

				const preselectedLocation = searchParams.get("locationId");
				if (preselectedLocation) {
					setLocationId(Number(preselectedLocation));
				}
			} catch {
				message.error("Failed to load locations");
			} finally {
				setLoadingLocations(false);
			}
		})();
	}, [searchParams]);

	// When location changes, load assets for that location
	useEffect(() => {
		setAssets([]);
		setAssetId(null);

		if (!locationId) return;

		setLoadingAssets(true);
		getAssetsByLocation(locationId)
			.then((result) => {
				const mapped: AssetOption[] = (result.assets || []).map((a) => ({
					id: a.id,
					label: `${a.assetTag ?? "No Tag"}${a.typeName ? ` — ${a.typeName}` : ""}`,
				}));
				setAssets(mapped);

				const preselectedAsset = searchParams.get("assetId");
				if (preselectedAsset) {
					setAssetId(Number(preselectedAsset));
				}
			})
			.catch(() => {})
			.finally(() => setLoadingAssets(false));
	}, [locationId, searchParams]);

	// Load users and vendors when assignment step is reached
	useEffect(() => {
		if (currentStep >= 2 && users.length === 0) {
			setLoadingUsers(true);
			Promise.all([
				getOrganizationUsers().catch(() => []),
				listVendors().catch(() => ({ vendors: [] })),
				listMyVendors().catch(() => []),
			]).then(([userData, vendorData, flData]) => {
				const mapped: UserOption[] = (userData || []).map((u: any) => ({
					id: u.id,
					label: `${u.first_name || u.firstName || ""} ${u.last_name || u.lastName || ""}`.trim() || u.email,
					email: u.email,
				}));
				setUsers(mapped);
				setVendors(vendorData.vendors || []);
				setFlVendors(flData || []);
			}).finally(() => setLoadingUsers(false));
		}
	}, [currentStep, users.length]);

	const handleSubmit = async () => {
		if (!locationId) {
			message.warning("Please select a location");
			setCurrentStep(0);
			return;
		}
		if (!title.trim()) {
			message.warning("Please enter a title for the work order");
			setCurrentStep(1);
			return;
		}

		setSubmitting(true);
		try {
			const wo = await createWorkOrder({
				locationId,
				assetId: assetId || undefined,
				title: title.trim(),
				description: description.trim() || undefined,
				workType,
				priority,
				assignedToUserId: assigneeType === "user" ? assignedToUserId : undefined,
				assignedToVendorId: assigneeType === "vendor" ? assignedToVendorId : undefined,
				assignedToFlOrgVendorId: assigneeType === "fl_vendor" ? assignedToFlOrgVendorId : undefined,
				dueDate: dueDate ? dueDate.toISOString() : undefined,
				estimatedCost: estimatedCost || undefined,
				estimatedLaborHours: estimatedLaborHours || undefined,
			});
			message.success("Work order created successfully");
			navigate(`/work-orders/${wo.id}`);
		} catch (error: any) {
			message.error(error.message || "Failed to create work order");
		} finally {
			setSubmitting(false);
		}
	};

	const canProceedFromStep = (step: number): boolean => {
		switch (step) {
			case 0: return !!locationId;
			case 1: return !!title.trim();
			case 2: return true;
			default: return true;
		}
	};

	const nextStep = () => {
		if (canProceedFromStep(currentStep)) {
			setCurrentStep((s) => Math.min(s + 1, 3));
		} else if (currentStep === 0) {
			message.warning("Please select a location");
		} else if (currentStep === 1) {
			message.warning("Please enter a title");
		}
	};

	const prevStep = () => setCurrentStep((s) => Math.max(s - 1, 0));

	return (
		<div className="wo-create-page">
			<div className="wo-create-header">
				<Button
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate("/work-orders")}
				>
					Back to Work Orders
				</Button>
				<h1>
					<PlusOutlined /> New Work Order
				</h1>
			</div>

			<div className="wo-create-layout">
				<div className="wo-create-steps">
					<Steps
						current={currentStep}
						onChange={(step) => {
							if (step < currentStep || canProceedFromStep(currentStep)) {
								setCurrentStep(step);
							}
						}}
						direction="vertical"
						items={[
							{
								title: "Location",
								description: locationId
									? locations.find((l) => l.id === locationId)?.label || "Selected"
									: "Where is the issue?",
								icon: <EnvironmentOutlined />,
							},
							{
								title: "Details",
								description: title ? title.substring(0, 40) : "What needs to be done?",
								icon: <FileTextOutlined />,
							},
							{
								title: "Assignment",
								description: assigneeType !== "none" ? "Assigned" : "Who should handle this?",
								icon: <UserOutlined />,
							},
							{
								title: "Review & Submit",
								description: "Confirm and create",
								icon: <CheckCircleOutlined />,
							},
						]}
					/>
				</div>

				<div className="wo-create-form">
					{/* Step 0: Location & Asset */}
					{currentStep === 0 && (
						<Card className="wo-create-card">
							<h2><EnvironmentOutlined /> Where is the issue?</h2>
							<p className="wo-create-subtitle">Select the location and optionally the specific equipment.</p>

							<div className="wo-field">
								<label className="wo-label">
									Location <span className="wo-required">*</span>
								</label>
								<Select
									showSearch
									placeholder="Search for a location..."
									value={locationId}
									onChange={(v) => {
										setLocationId(v);
										setAssetId(null);
									}}
									loading={loadingLocations}
									size="large"
									style={{ width: "100%" }}
									filterOption={(input, option) =>
										(option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
									}
									options={locations.map((loc) => ({
										value: loc.id,
										label: loc.label,
										description: loc.address,
									}))}
									optionRender={(option) => (
										<div>
											<div style={{ fontWeight: 500 }}>{option.label}</div>
											{(option.data as any).description && (
												<div style={{ fontSize: 12, color: "#888" }}>
													{(option.data as any).description}
												</div>
											)}
										</div>
									)}
								/>
							</div>

							{locationId && (
								<div className="wo-field">
									<label className="wo-label">
										Equipment / Asset <span className="wo-optional">(optional)</span>
									</label>
									<Select
										showSearch
										allowClear
										placeholder={loadingAssets ? "Loading assets..." : assets.length === 0 ? "No assets at this location" : "Select specific equipment..."}
										value={assetId}
										onChange={setAssetId}
										loading={loadingAssets}
										disabled={loadingAssets || assets.length === 0}
										size="large"
										style={{ width: "100%" }}
										filterOption={(input, option) =>
											(option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
										}
										options={assets.map((a) => ({
											value: a.id,
											label: a.label,
										}))}
									/>
								</div>
							)}

							<div className="wo-step-actions">
								<div />
								<Button type="primary" size="large" onClick={nextStep} disabled={!locationId}>
									Continue
								</Button>
							</div>
						</Card>
					)}

					{/* Step 1: Work Order Details */}
					{currentStep === 1 && (
						<Card className="wo-create-card">
							<h2><FileTextOutlined /> What needs to be done?</h2>
							<p className="wo-create-subtitle">Describe the issue and set its urgency.</p>

							<div className="wo-field">
								<label className="wo-label">
									Title <span className="wo-required">*</span>
								</label>
								<Input
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									placeholder="e.g., HVAC unit not cooling on 2nd floor"
									size="large"
									maxLength={200}
									count={{ show: true, max: 200 }}
								/>
							</div>

							<div className="wo-field-row">
								<div className="wo-field wo-field-half">
									<label className="wo-label">
										Work Type <span className="wo-required">*</span>
									</label>
									<Select
										value={workType}
										onChange={setWorkType}
										size="large"
										style={{ width: "100%" }}
										optionRender={(option) => {
											const wt = WORK_TYPES.find((w) => w.value === option.value);
											return (
												<div>
													<div style={{ fontWeight: 500 }}>{option.label}</div>
													{wt && <div style={{ fontSize: 12, color: "#888" }}>{wt.description}</div>}
												</div>
											);
										}}
										options={WORK_TYPES.map((wt) => ({
											value: wt.value,
											label: wt.label,
										}))}
									/>
								</div>
								<div className="wo-field wo-field-half">
									<label className="wo-label">
										Priority <span className="wo-required">*</span>
									</label>
									<Select
										value={priority}
										onChange={setPriority}
										size="large"
										style={{ width: "100%" }}
									>
										{PRIORITIES.map((p) => (
											<Select.Option key={p.value} value={p.value}>
												<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
													<span style={{ color: p.color, fontWeight: 600 }}>{p.label}</span>
													<span style={{ fontSize: 11, color: "#999" }}>{p.sla}</span>
												</div>
											</Select.Option>
										))}
									</Select>
								</div>
							</div>

							<div className="wo-field">
								<label className="wo-label">
									Description <span className="wo-optional">(optional)</span>
								</label>
								<TextArea
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="Provide additional details about the issue, affected area, symptoms observed, any temporary measures taken..."
									rows={4}
									maxLength={2000}
									count={{ show: true, max: 2000 }}
								/>
							</div>

							<div className="wo-step-actions">
								<Button size="large" onClick={prevStep}>Back</Button>
								<Button type="primary" size="large" onClick={nextStep} disabled={!title.trim()}>
									Continue
								</Button>
							</div>
						</Card>
					)}

					{/* Step 2: Assignment & Scheduling */}
					{currentStep === 2 && (
						<Card className="wo-create-card">
							<h2><UserOutlined /> Who should handle this?</h2>
							<p className="wo-create-subtitle">Assign a team member or vendor, and set a timeline.</p>

							<div className="wo-field">
								<label className="wo-label">Assign To</label>
								<Select
									value={assigneeType}
									onChange={(v: AssigneeType) => {
										setAssigneeType(v);
										setAssignedToUserId(null);
										setAssignedToVendorId(null);
										setAssignedToFlOrgVendorId(null);
									}}
									size="large"
									style={{ width: "100%" }}
									options={[
										{ value: "none", label: "Leave Unassigned" },
										{ value: "user", label: "Internal Team Member" },
										{ value: "vendor", label: "External Vendor" },
										{ value: "fl_vendor", label: "FacilityLink Vendor Network" },
									]}
								/>
							</div>

							{assigneeType === "user" && (
								<div className="wo-field">
									<label className="wo-label">Team Member</label>
									<Select
										showSearch
										placeholder="Select a team member..."
										value={assignedToUserId}
										onChange={setAssignedToUserId}
										loading={loadingUsers}
										size="large"
										style={{ width: "100%" }}
										filterOption={(input, option) =>
											(option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
										}
										options={users.map((u) => ({
											value: u.id,
											label: u.label,
											description: u.email,
										}))}
										optionRender={(option) => (
											<div>
												<div style={{ fontWeight: 500 }}>{option.label}</div>
												<div style={{ fontSize: 12, color: "#888" }}>{(option.data as any).description}</div>
											</div>
										)}
									/>
								</div>
							)}

							{assigneeType === "vendor" && (
								<div className="wo-field">
									<label className="wo-label">Vendor</label>
									<Select
										showSearch
										placeholder="Select a vendor..."
										value={assignedToVendorId}
										onChange={setAssignedToVendorId}
										loading={loadingUsers}
										size="large"
										style={{ width: "100%" }}
										filterOption={(input, option) =>
											(option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
										}
										options={vendors.map((v) => ({
											value: v.id,
											label: v.name,
											description: v.specialty || "",
										}))}
										optionRender={(option) => (
											<div>
												<div style={{ fontWeight: 500 }}>{option.label}</div>
												{(option.data as any).description && (
													<div style={{ fontSize: 12, color: "#888" }}>{(option.data as any).description}</div>
												)}
											</div>
										)}
									/>
								</div>
							)}

							{assigneeType === "fl_vendor" && (
								<div className="wo-field">
									<label className="wo-label">FacilityLink Vendor</label>
									<Select
										showSearch
										placeholder="Select from your vendor network..."
										value={assignedToFlOrgVendorId}
										onChange={setAssignedToFlOrgVendorId}
										loading={loadingUsers}
										size="large"
										style={{ width: "100%" }}
										filterOption={(input, option) =>
											(option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
										}
										options={flVendors.map((v) => ({
											value: v.id,
											label: v.companyName,
											description: v.trades?.map((t) => t.name).join(", ") || "",
										}))}
										optionRender={(option) => (
											<div>
												<div style={{ fontWeight: 500 }}>{option.label}</div>
												{(option.data as any).description && (
													<div style={{ fontSize: 12, color: "#888" }}>{(option.data as any).description}</div>
												)}
											</div>
										)}
									/>
								</div>
							)}

							<Divider />

							<div className="wo-field-row">
								<div className="wo-field wo-field-third">
									<label className="wo-label">
										Due Date <span className="wo-optional">(optional)</span>
									</label>
									<DatePicker
										value={dueDate}
										onChange={setDueDate}
										size="large"
										style={{ width: "100%" }}
										format="MMM D, YYYY"
									/>
								</div>
								<div className="wo-field wo-field-third">
									<label className="wo-label">
										Est. Cost <span className="wo-optional">(optional)</span>
									</label>
									<InputNumber
										value={estimatedCost}
										onChange={setEstimatedCost}
										size="large"
										style={{ width: "100%" }}
										min={0}
										prefix="$"
										placeholder="0.00"
									/>
								</div>
								<div className="wo-field wo-field-third">
									<label className="wo-label">
										Est. Hours <span className="wo-optional">(optional)</span>
									</label>
									<InputNumber
										value={estimatedLaborHours}
										onChange={setEstimatedLaborHours}
										size="large"
										style={{ width: "100%" }}
										min={0}
										placeholder="0"
									/>
								</div>
							</div>

							<div className="wo-step-actions">
								<Button size="large" onClick={prevStep}>Back</Button>
								<Button type="primary" size="large" onClick={nextStep}>
									Review
								</Button>
							</div>
						</Card>
					)}

					{/* Step 3: Review & Submit */}
					{currentStep === 3 && (
						<Card className="wo-create-card">
							<h2><CheckCircleOutlined /> Review & Submit</h2>
							<p className="wo-create-subtitle">Confirm the details below, then create the work order.</p>

							<div className="wo-review-grid">
								<div className="wo-review-section">
									<h3>Location</h3>
									<div className="wo-review-item">
										<span className="wo-review-label">Location</span>
										<span className="wo-review-value">
											{locations.find((l) => l.id === locationId)?.label || "—"}
										</span>
									</div>
									{assetId && (
										<div className="wo-review-item">
											<span className="wo-review-label">Equipment</span>
											<span className="wo-review-value">
												{assets.find((a) => a.id === assetId)?.label || `Asset #${assetId}`}
											</span>
										</div>
									)}
								</div>

								<div className="wo-review-section">
									<h3>Work Details</h3>
									<div className="wo-review-item">
										<span className="wo-review-label">Title</span>
										<span className="wo-review-value">{title}</span>
									</div>
									<div className="wo-review-item">
										<span className="wo-review-label">Type</span>
										<span className="wo-review-value">{workType}</span>
									</div>
									<div className="wo-review-item">
										<span className="wo-review-label">Priority</span>
										<span className="wo-review-value" style={{ color: PRIORITIES.find((p) => p.value === priority)?.color }}>
											{PRIORITIES.find((p) => p.value === priority)?.label}
										</span>
									</div>
									{description && (
										<div className="wo-review-item">
											<span className="wo-review-label">Description</span>
											<span className="wo-review-value wo-review-description">{description}</span>
										</div>
									)}
								</div>

								<div className="wo-review-section">
									<h3>Assignment & Schedule</h3>
									<div className="wo-review-item">
										<span className="wo-review-label">Assigned To</span>
										<span className="wo-review-value">
											{assigneeType === "user"
												? users.find((u) => u.id === assignedToUserId)?.label || "—"
												: assigneeType === "vendor"
													? vendors.find((v) => v.id === assignedToVendorId)?.name || "—"
													: assigneeType === "fl_vendor"
														? flVendors.find((v) => v.id === assignedToFlOrgVendorId)?.companyName || "—"
														: "Unassigned"}
										</span>
									</div>
									<div className="wo-review-item">
										<span className="wo-review-label">Due Date</span>
										<span className="wo-review-value">
											{dueDate ? dueDate.format("MMM D, YYYY") : "Not set"}
										</span>
									</div>
									{estimatedCost != null && estimatedCost > 0 && (
										<div className="wo-review-item">
											<span className="wo-review-label">Est. Cost</span>
											<span className="wo-review-value">${estimatedCost.toLocaleString()}</span>
										</div>
									)}
									{estimatedLaborHours != null && estimatedLaborHours > 0 && (
										<div className="wo-review-item">
											<span className="wo-review-label">Est. Hours</span>
											<span className="wo-review-value">{estimatedLaborHours}h</span>
										</div>
									)}
								</div>
							</div>

							<div className="wo-step-actions">
								<Button size="large" onClick={prevStep}>Back</Button>
								<Button
									type="primary"
									size="large"
									icon={<PlusOutlined />}
									loading={submitting}
									onClick={handleSubmit}
								>
									Create Work Order
								</Button>
							</div>
						</Card>
					)}
				</div>
			</div>
		</div>
	);
};

export { WorkOrderCreatePage };
